// Centralized business-context helpers: the merchant profile is the single source
// of truth. Derives the activity family/group and returns métier-aware, localized
// Command Center suggestions, greeting and guided-tour example — dynamic & extensible
// (grouped by business model, so new métiers inherit the right content automatically).
import { FAMILIES, pick } from "./industries";

// Family id -> suggestion group (business model). Extensible: add a family here and
// it inherits the group's localized content. Unknown families fall back to "generic".
const FAMILY_GROUP = {
  resto: "food_venue", alim: "food_retail", detail: "retail",
  artisan: "trades", auto: "trades",
  beaute: "appt", coiffure: "appt", sante: "appt", sport: "appt",
  hotel: "hospitality", tourisme: "hospitality",
  serv_part: "services", serv_pro: "services", immo: "services",
  edu: "services", event: "services", liberal: "services", freelance: "services",
  autre: "generic",
};

// Demo profile id -> family id.
const DEMO_FAMILY = {
  boulangerie: "alim", coiffure: "coiffure", restaurant: "resto", plombier: "artisan",
  boutique: "detail", cafe: "resto", hotel: "hotel", institut: "beaute",
};

// Reverse lookup: métier label (any language, lowercased) -> family id.
let _labelToFamily = null;
function labelToFamily() {
  if (_labelToFamily) return _labelToFamily;
  _labelToFamily = {};
  for (const fam of FAMILIES) {
    for (const [, lbl] of fam.metiers) {
      ["fr", "en", "es"].forEach((l) => { _labelToFamily[(lbl[l] || "").toLowerCase()] = fam.id; });
    }
    ["fr", "en", "es"].forEach((l) => { _labelToFamily[(fam.label[l] || "").toLowerCase()] = fam.id; });
  }
  return _labelToFamily;
}

export function resolveFamily(data) {
  const o = data?.onboarding || {};
  if (o.activity_family && FAMILY_GROUP[o.activity_family]) return o.activity_family;
  if (data?.demoProfileId && DEMO_FAMILY[data.demoProfileId]) return DEMO_FAMILY[data.demoProfileId];
  const at = (o.activity_type || "").toLowerCase().trim();
  if (at && labelToFamily()[at]) return labelToFamily()[at];
  return "autre";
}

const SUGGESTIONS = {
  food_venue: {
    fr: ["Comment remplir mon établissement aux heures creuses ?", "Prépare une animation pour ce week-end.", "Crée une offre pour augmenter mon panier moyen.", "Comment obtenir plus d'avis clients ?"],
    en: ["How can I fill my venue during off-peak hours?", "Prepare an event for this weekend.", "Create an offer to raise my average spend.", "How can I get more customer reviews?"],
    es: ["¿Cómo llenar mi local en horas flojas?", "Prepara una animación para este fin de semana.", "Crea una oferta para subir mi ticket medio.", "¿Cómo conseguir más reseñas de clientes?"],
  },
  food_retail: {
    fr: ["Comment augmenter mes ventes aux heures creuses ?", "Prépare une opération fidélité.", "Crée une promotion pour une nouveauté.", "Comment augmenter mon panier moyen ?"],
    en: ["How can I boost sales during quiet hours?", "Prepare a loyalty campaign.", "Create a promotion for a new product.", "How can I increase my average basket?"],
    es: ["¿Cómo aumentar ventas en horas flojas?", "Prepara una acción de fidelización.", "Crea una promoción para una novedad.", "¿Cómo aumentar mi ticket medio?"],
  },
  retail: {
    fr: ["Comment attirer plus de clients en boutique ?", "Prépare une vente privée pour écouler mes stocks.", "Crée une campagne pour ma nouvelle collection.", "Comment réactiver mes clients inactifs ?"],
    en: ["How can I drive more foot traffic to my store?", "Prepare a private sale to clear stock.", "Create a campaign for my new collection.", "How can I reactivate my inactive customers?"],
    es: ["¿Cómo atraer más clientes a mi tienda?", "Prepara una venta privada para liquidar stock.", "Crea una campaña para mi nueva colección.", "¿Cómo reactivar a mis clientes inactivos?"],
  },
  trades: {
    fr: ["Comment obtenir davantage de demandes de devis ?", "Prépare une relance pour mes devis en attente.", "Comment obtenir plus d'avis après intervention ?", "Prépare une publication avant/après chantier."],
    en: ["How can I get more quote requests?", "Prepare a follow-up for my pending quotes.", "How can I get more reviews after a job?", "Prepare a before/after job post."],
    es: ["¿Cómo conseguir más solicitudes de presupuesto?", "Prepara un seguimiento de mis presupuestos pendientes.", "¿Cómo conseguir más reseñas tras una intervención?", "Prepara una publicación de antes/después de obra."],
  },
  appt: {
    fr: ["Comment remplir mes créneaux les plus calmes ?", "Prépare une relance pour mes clients absents.", "Promeus une prestation premium.", "Comment obtenir plus d'avis après rendez-vous ?"],
    en: ["How can I fill my quietest time slots?", "Prepare a win-back for my absent clients.", "Promote a premium service.", "How can I get more reviews after appointments?"],
    es: ["¿Cómo llenar mis horas más tranquilas?", "Prepara una reactivación para mis clientes ausentes.", "Promociona un servicio premium.", "¿Cómo conseguir más reseñas tras las citas?"],
  },
  hospitality: {
    fr: ["Comment augmenter mes réservations en semaine ?", "Prépare une offre pour remplir mes disponibilités ce week-end.", "Comment augmenter mes réservations directes ?", "Aide-moi à obtenir davantage d'avis voyageurs."],
    en: ["How can I increase my weekday bookings?", "Prepare an offer to fill my availability this weekend.", "How can I increase my direct bookings?", "Help me get more traveler reviews."],
    es: ["¿Cómo aumentar mis reservas entre semana?", "Prepara una oferta para llenar mi disponibilidad este fin de semana.", "¿Cómo aumentar mis reservas directas?", "Ayúdame a conseguir más reseñas de viajeros."],
  },
  services: {
    fr: ["Comment obtenir plus de demandes de contact ?", "Prépare une relance pour mes prospects.", "Comment renforcer ma présence en ligne ?", "Comment obtenir plus de recommandations clients ?"],
    en: ["How can I get more contact requests?", "Prepare a follow-up for my leads.", "How can I strengthen my online presence?", "How can I get more client referrals?"],
    es: ["¿Cómo conseguir más solicitudes de contacto?", "Prepara un seguimiento de mis prospectos.", "¿Cómo reforzar mi presencia online?", "¿Cómo conseguir más recomendaciones de clientes?"],
  },
  generic: {
    fr: ["Comment attirer plus de clients ?", "Prépare une animation commerciale pour cette semaine.", "Crée ma campagne réseaux de la semaine.", "Comment récupérer mes clients inactifs ?"],
    en: ["How can I attract more customers?", "Prepare a commercial event for this week.", "Create my social campaign for the week.", "How can I win back my inactive customers?"],
    es: ["¿Cómo atraer más clientes?", "Prepara una acción comercial para esta semana.", "Crea mi campaña en redes de la semana.", "¿Cómo recuperar a mis clientes inactivos?"],
  },
};

const TOUR_EX = {
  food_venue: { fr: "Comment remplir mon établissement le mardi soir ?", en: "How do I fill my venue on Tuesday evenings?", es: "¿Cómo llenar mi local los martes por la noche?" },
  food_retail: { fr: "Prépare une opération fidélité pour mes habitués.", en: "Prepare a loyalty campaign for my regulars.", es: "Prepara una acción de fidelización para mis habituales." },
  retail: { fr: "Crée une campagne pour ma nouvelle collection.", en: "Create a campaign for my new collection.", es: "Crea una campaña para mi nueva colección." },
  trades: { fr: "Prépare une relance pour mes devis en attente.", en: "Prepare a follow-up for my pending quotes.", es: "Prepara un seguimiento de mis presupuestos pendientes." },
  appt: { fr: "Prépare une relance pour mes clients absents.", en: "Prepare a win-back for my absent clients.", es: "Prepara una reactivación para mis clientes ausentes." },
  hospitality: { fr: "Prépare une offre pour remplir mes chambres en semaine.", en: "Prepare an offer to fill my rooms on weekdays.", es: "Prepara una oferta para llenar mis habitaciones entre semana." },
  services: { fr: "Comment obtenir plus de demandes de contact ?", en: "How do I get more contact requests?", es: "¿Cómo conseguir más solicitudes de contacto?" },
  generic: { fr: "Comment attirer plus de clients cette semaine ?", en: "How do I attract more customers this week?", es: "¿Cómo atraer más clientes esta semana?" },
};

function group(data) { return FAMILY_GROUP[resolveFamily(data)] || "generic"; }

export function getSuggestions(data, lang) {
  const g = SUGGESTIONS[group(data)] || SUGGESTIONS.generic;
  return g[lang] || g.fr;
}

export function getGreeting(data, lang) {
  const name = data?.onboarding?.business_name;
  const hello = { fr: "Bonjour", en: "Hello", es: "Hola" }[lang] || "Bonjour";
  const body = {
    fr: "Je suis votre copilote LOCALBOOST. Décrivez votre situation ou un objectif, et je vous prépare un plan d'action prêt à lancer. 🚀",
    en: "I'm your LOCALBOOST copilot. Describe your situation or a goal, and I'll prepare a ready-to-launch action plan. 🚀",
    es: "Soy tu copiloto LOCALBOOST. Describe tu situación o un objetivo y te preparo un plan de acción listo para lanzar. 🚀",
  }[lang] || "";
  return `${hello}${name ? ` ${name}` : ""} ! ${body}`;
}

export function getTourExample(data, lang) {
  const ex = TOUR_EX[group(data)] || TOUR_EX.generic;
  const label = { fr: "Par exemple : ", en: "For example: ", es: "Por ejemplo: " }[lang] || "";
  return `${label}« ${ex[lang] || ex.fr} »`;
}
